import { siteApiSlice } from "../../app/siteApiSlice";
import { UserRole } from "../../app/userSlice";
import { SelectListItem } from "../../components/input/SelectList";
import { DataTablesQueryModel, DataTablesViewModel } from "../../interfaces/dataTables";

export interface AlertViewModel {
	dT_RowId: string;
	message: string;
	expireDate: string;
	isRead: boolean;
}

export interface BroadcastAlertViewModel {
	dT_RowId: number;
	message: string;
	beginDate?: Date;
	expireDate: Date;
	districts: BroadcastAlertDistrictViewModel[];
	schools: BroadcastAlertSchoolViewModel[];
	roles: UserRole[];
}

export interface BroadcastAlertDistrictViewModel {
	name: string;
	id: string;
}

export interface BroadcastAlertSchoolViewModel {
	name: string;
	districtName: string;
	id: string;
	districtId: string;
}

export interface AddEditBroadcastAlertFormViewModel extends BaseAddEditBroadcastAlertFormViewModel {
	districts: SelectListItem[];
	schools: BroadcastAlertSchoolViewModel[];
	roles: SelectListItem[];
}

export interface BaseAddEditBroadcastAlertFormViewModel {
	message: string;
	beginDate?: Date;
	expireDate: Date;
	districtIds: number[];
	schoolIds: number[];
	roleValues: string[];
}

export const alertsApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		getMyAlerts: build.query<DataTablesViewModel<AlertViewModel>, DataTablesQueryModel>({
			query: (q) => {
				const { iDisplayStart, iDisplayLength } = q;
				return {
					url: `/Alerts/MyAlertsAjaxHandler`,
					params: { iDisplayStart, iDisplayLength },
				};
			},
			providesTags: ["Alerts"],
		}),
		hideAlert: build.mutation<boolean, number>({
			query: (id) => ({
				url: `/Alerts/MarkReadOnAlert`,
				params: { id },
				method: "GET",
			}),
			invalidatesTags: ["Alerts"],
		}),
		hideAlerts: build.mutation<boolean, number[]>({
			query: (ids) => {
				const args = `ids=${ids.join("&ids=")}`;
				return {
					url: `/Alerts/MarkReadOnAlerts?${args}`,
					method: "GET",
				};
			},
			invalidatesTags: ["Alerts"],
		}),
		hideAllAlerts: build.mutation<boolean, void>({
			query: () => ({
				url: `/Alerts/MarkReadOnAllAlerts`,
				method: "GET",
			}),
			invalidatesTags: ["Alerts"],
		}),
		restoreAlert: build.mutation<boolean, number>({
			query: (id) => ({
				url: `/Alerts/UnmarkReadOnAlert`,
				params: { id },
				method: "GET",
			}),
			invalidatesTags: ["Alerts"],
		}),
		getAdminAlerts: build.query<DataTablesViewModel<BroadcastAlertViewModel>, void>({
			query: () => {
				return {
					url: `/Alerts/BroadcastAlertsList`,
					method: "GET",
				};
			},
			providesTags: ["Alerts"],
		}),
		getAddAdminAlertsData: build.query<AddEditBroadcastAlertFormViewModel, void>({
			query: () => {
				return {
					url: `/Alerts/Add`,
					method: "GET",
				};
			},
		}),
		addAdminAlert: build.mutation<void, BaseAddEditBroadcastAlertFormViewModel>({
			query: (model) => {
				return {
					url: `/Alerts/Add`,
					method: "POST",
					body: { model },
				};
			},
			invalidatesTags: ["Alerts"],
		}),
		getEditAdminAlertData: build.query<AddEditBroadcastAlertFormViewModel, number>({
			query: (id) => {
				return {
					url: `/Alerts/Edit`,
					params: { id },
					method: "GET",
				};
			},
			providesTags: ["Alerts"],
		}),
		editAdminAlert: build.mutation<
			void,
			{ id: number; model: BaseAddEditBroadcastAlertFormViewModel }
		>({
			query: ({ id, model }) => {
				return {
					url: `/Alerts/Edit`,
					method: "POST",
					body: { id, model },
				};
			},
			invalidatesTags: ["Alerts"],
		}),
		deleteAdminAlerts: build.mutation<void, number[]>({
			query: (data) => {
				return {
					url: `/Alerts/Delete`,
					method: "POST",
					body: data,
				};
			},
			invalidatesTags: ["Alerts"],
		}),
	}),
});

export const {
	useGetMyAlertsQuery,
	useHideAlertMutation,
	useHideAlertsMutation,
	useHideAllAlertsMutation,
	useRestoreAlertMutation,
	useGetAdminAlertsQuery,
	useGetAddAdminAlertsDataQuery,
	useGetEditAdminAlertDataQuery,
	useAddAdminAlertMutation,
	useEditAdminAlertMutation,
	useDeleteAdminAlertsMutation,
} = alertsApiSlice;
